'use client'

import { C, Callout, Exercise, H, Lesson, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/code-extra'

export default function ProjectTextStats() {
  return (
    <Lesson>
      <Step>
        <H>Your first project</H>
        <P>
          Time to use everything from this module in one program. You’ll analyse a small collection of stories (it’s built in as{' '}
          <C>data.stories</C>) in three parts:
        </P>
        <OL>
          <li>Turn raw text into a clean list of words.</li>
          <li>Find the most common words and chart them.</li>
          <li>Build a table of which word follows which — and use it to write a sentence. That’s a (tiny) language model.</li>
        </OL>
        <Callout kind="note">
          Projects are bigger than lessons. Expect to get stuck, read error messages, add <C>console.log</C> lines to look at your
          data, and try things. That’s not a sign you’re doing it wrong — it’s what programming is.
        </Callout>
      </Step>

      <Step gate="text-words">
        <H>Part 1 · From text to words</H>
        <P>
          Real text is messy: capital letters, full stops, commas, line breaks. Before counting anything, normalise it. The hint
          uses a <strong>regular expression</strong> — a mini-language for patterns in text. <C>{'/[^a-z]+/g'}</C> means “one or
          more characters that are <em>not</em> a lower-case letter, everywhere”.
        </P>
        <Exercise
          id="text-words"
          title="words(text)"
          {...EX.textWords}
          hints={[
            <>
              Chain it: <C>{'text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="text-top">
        <H>Part 2 · The most common words</H>
        <P>
          Count with a Map, turn it into an array of <C>[word, count]</C> pairs, sort by count (biggest first), and keep the top
          k. For ties, sort alphabetically so your output is predictable.
        </P>
        <Exercise
          id="text-top"
          title="topWords(ws, k)"
          {...EX.textTop}
          hints={[
            <>
              <C>[...counts.entries()]</C> turns a Map into an array of pairs you can sort.
            </>,
            <>
              Comparator with a tie-breaker:{' '}
              <C>{'(a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1)'}</C> — the <C>||</C> only kicks in when the counts are equal (
              difference 0).
            </>,
          ]}
        />
      </Step>

      <Step gate="text-next">
        <H>Part 3 · What comes next?</H>
        <P>
          For every word, count the words that come right after it. Then, starting from “the”, keep choosing the most frequent
          follower. You’re building a <strong>map of maps</strong>: <C>{'Map<word, Map<nextWord, count>>'}</C>.
        </P>
        <Exercise
          id="text-next"
          title="followers and mostLikelyNext"
          {...EX.textNext}
          hints={[
            <>
              For each position i: <C>{'const row = table.get(ws[i]) ?? new Map()'}</C>, bump <C>row</C>’s count for{' '}
              <C>ws[i + 1]</C>, then <C>table.set(ws[i], row)</C>.
            </>,
            <>Look up the row for the word; loop over its entries and keep the follower with the biggest count.</>,
          ]}
        />
        <Callout kind="why">
          Your generator probably gets stuck in a loop like “the little fox the little fox…”. Always picking the single most likely
          next word does that. Language models fix it by <em>sampling</em> instead of always taking the top choice — and by
          looking at much more than one previous word. Both are coming in Module 8.
        </Callout>
      </Step>

      <Step>
        <H>Stretch goals</H>
        <P>If you want to keep going (no checks — just explore in the editor above or in the Lab):</P>
        <UL>
          <li>Print the 10 longest distinct words.</li>
          <li>Find the most common <em>pair</em> of adjacent words in the whole text.</li>
          <li>
            Make the generator pick a random follower instead of the top one (<C>random.choice</C> on a list where each follower
            appears as many times as it was counted). Run it a few times — does it write more varied sentences?
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
